import { autoUpdate, flip, offset, size } from "@floating-ui/dom";
import { gapCursor } from "prosemirror-gapcursor";
import { history, redo, undo } from "prosemirror-history";
import { keymap } from "prosemirror-keymap";
import type { Node } from "prosemirror-model";
import { EditorState, Plugin, TextSelection } from "prosemirror-state";
import type { EditorView, NodeView } from "prosemirror-view";
import type { User } from "sdk";
import { useFloating } from "solid-floating-ui";
import {
	type Accessor,
	createEffect,
	createMemo,
	createSignal,
	getOwner,
	onMount,
	runWithOwner,
	Show,
} from "solid-js";
import { Portal, render } from "solid-js/web";
import {
	useChannels,
	useMessages,
	useRoles,
	useRoomMembers,
	useThreadMembers,
	useUsers,
} from "@/api";
import icSearch from "@/assets/search.png";
import { Icon } from "@/atoms/Icon";
import {
	createEditor as createBaseEditor,
	createPlaceholderPlugin,
} from "@/components/features/editor/mod.tsx";
import { type SearchState, useSearch } from "@/contexts/search";
import type { RoomT, ThreadT } from "@/types";
import { SEARCH_FILTERS } from "./filters.config";
import {
	type ActiveFilter,
	autocompletePlugin,
	getFilterFromSelection,
	syntaxHighlightingPlugin,
} from "./plugins";
import { type Completion, SearchAutocomplete } from "./SearchAutocomplete";
import { FilterChip } from "./SearchFilterChip";
import { schema } from "./schema";
import { buildBackendSearchBody } from "./searchCompiler";
import type { LabelPart } from "./types";
import {
	addRecentSearch,
	formatRecentSearch,
	getRecentSearches,
	parseQueryToNodes,
	serializeToQuery,
} from "./utils";

// ---------------------------------------------------------------------------
// NodeView factory for rendering filter chips inside ProseMirror
// ---------------------------------------------------------------------------

const createFilterNodeView = (
	type: string,
	roomId: string | null,
	channelId: string | undefined,
	owner: ReturnType<typeof getOwner>,
	animate: Accessor<boolean>,
) => {
	return (node: Node): NodeView => {
		const dom = document.createElement("span");

		const getId = () => node.attrs.id || node.attrs.value || node.attrs.date;
		const getName = () => node.attrs.name as string | undefined;
		const getNegated = () => node.attrs.negated as boolean;

		const dispose = render(
			() =>
				runWithOwner(owner, () => (
					<FilterChip
						type={type}
						id={getId()}
						name={getName()}
						negated={getNegated()}
						animate={animate()}
						roomId={roomId}
						channelId={channelId}
					/>
				)),
			dom,
		);

		return {
			dom,
			update: (newNode: Node) => {
				if (newNode.type !== node.type) return false;
				node = newNode;
				return true;
			},
			destroy: () => dispose(),
		};
	};
};

export const SearchInput = (props: {
	channel?: ThreadT;
	room?: RoomT;
	autofocus?: boolean;
	value?: string;
	onInput?: (query: string) => void;
}) => {
	const users = useUsers();
	const messagesService = useMessages();
	const owner = getOwner();
	const [dropdownRef, setDropdownRef] = createSignal<HTMLDivElement>();
	const [activeFilter, setActiveFilter] = createSignal<ActiveFilter | null>(
		null,
	);

	const [hoveredIndex, setHoveredIndex] = createSignal<number>(0);
	const [editorRef, setEditorRef] = createSignal<HTMLElement>();
	const [editorFocused, setEditorFocused] = createSignal(false);
	const [isFirstFocus, setIsFirstFocus] = createSignal(
		props.autofocus ?? false,
	);

	const allFilterSuggestions = [
		"author:",
		"channel:",
		"before:",
		"after:",
		"has:",
		"pinned:",
		"mentions:",
	];

	const filterSuggestions = createMemo(() => {
		const f = activeFilter();
		if (!f) return allFilterSuggestions;
		const query = f.query.toLowerCase();
		const negated = f.negated;
		if (!query) {
			return negated
				? allFilterSuggestions.map((f) => `-${f}`)
				: allFilterSuggestions;
		}

		return allFilterSuggestions
			.filter((f) => f.toLowerCase().includes(query))
			.map((f) => (negated ? `-${f}` : f));
	});

	const recentSearches = createMemo(() => {
		const f = activeFilter();
		if (f?.type === "filter" && f.query === "") {
			return getRecentSearches();
		}
		return [];
	});

	const autocompleteItems = createMemo(() => {
		const f = activeFilter();
		if (!f) return [];
		const type = f.type;
		const result: {
			id: string;
			label: string | LabelPart[];
			rawValue?: string;
			user?: User;
			channel?: ThreadT;
			onSelect: () => void;
			isSeparator?: boolean;
		}[] = [];

		if (type === "filter") {
			// Filter keyword suggestions
			filterSuggestions().forEach((filter) => {
				result.push({
					id: `filter-${filter}`,
					label: filter,
					onSelect: () => handleCompletion({ type: "text", text: filter }),
				});
			});

			// Recent searches
			const searches = recentSearches();
			if (searches.length > 0) {
				result.push({
					id: "recent-separator",
					label: "",
					onSelect: () => {},
					isSeparator: true,
				});
				searches.forEach((search, idx) => {
					result.push({
						id: `recent-${idx}`,
						label: formatRecentSearch(search, searchContext()),
						rawValue: search,
						onSelect: () =>
							handleCompletion({ type: "recent_search", query: search }),
					});
				});
			}
		} else {
			const def = SEARCH_FILTERS[type];
			if (!def) return result;

			const suggestions = def.getSuggestions(f.query, searchContext());

			suggestions.forEach((item) => {
				result.push({
					id: item.id,
					label: item.label,
					user: item.user,
					channel: item.channel,
					onSelect: () => {
						const astNode = {
							type,
							value: item.id.replace(`${type}-`, ""),
							name: item.label,
							negated: f.negated ?? false,
						};
						const pmNode = def.toPMNode(astNode);
						handleCompletion({ type: "node", node: pmNode });
					},
				});
			});
		}

		return result;
	});

	// Shared context object passed to autocomplete suggestions
	const searchContext = createMemo(() => {
		const channels = useChannels();
		const roomThreads = () =>
			[...channels.cache.values()].filter(
				(c) => c.room_id === (props.channel?.room_id ?? props.room?.id ?? ""),
			);
		return {
			users,
			channels,
			roomMembers: useRoomMembers(),
			threadMembers: useThreadMembers(),
			roles: useRoles(),
			roomThreads,
			roomId: props.channel?.room_id ?? props.room?.id ?? null,
			channel: props.channel,
			room: props.room,
		};
	});

	const position = useFloating(editorRef, dropdownRef, {
		whileElementsMounted: autoUpdate,
		middleware: [
			offset(4),
			flip(),
			size({
				padding: 16,
				apply({ availableHeight, elements }) {
					Object.assign(elements.floating.style, {
						maxHeight: `${Math.max(0, availableHeight)}px`,
					});
				},
			}),
		],
		placement: "bottom-start",
	});

	const hasSuggestions = createMemo(() => {
		const f = activeFilter();
		if (!f) return false;
		if (f.type === "filter") {
			return filterSuggestions().length > 0 || recentSearches().length > 0;
		}
		const def = SEARCH_FILTERS[f.type];
		if (!def) return false;
		return def.getSuggestions(f.query, searchContext()).length > 0;
	});

	const { states, setStates } = useSearch();

	const searchId = createMemo(
		() => props.channel?.id ?? props.room?.id ?? "global",
	);

	const currentSearch = () => states[searchId()];

	const updateSearch = (val: SearchState | undefined) => {
		if (val === undefined) {
			// HACK: close search results
			// ideally, i'd delete the property, but solidjs doesnt seem to work with that
			setStates(searchId(), undefined as any);
		} else {
			setStates(searchId(), val);
		}
	};

	createEffect(() => {
		const search = currentSearch();
		const view = editor.view;
		if (!view) return;

		if (!search && view.state.doc.content.size > 0) {
			const tr = view.state.tr.delete(0, view.state.doc.content.size);
			tr.setMeta("skipAutocomplete", true);
			view.dispatch(tr);
		}
	});

	createEffect((prevSort) => {
		const search = currentSearch();
		const currentSort = search?.sort;

		if (prevSort !== undefined && currentSort !== prevSort) {
			handleSubmit();
		}

		return currentSort;
	}, currentSearch()?.sort);

	/**
	 * Build the backend query using the compiler and execute the search.
	 * All backend-specific logic lives in `buildBackendSearchBody`.
	 */
	const handleSubmit = async () => {
		if (!editor.view) return;
		const queryString = serializeToQuery(editor.view.state);
		if (!queryString) {
			updateSearch(undefined);
			return;
		}

		addRecentSearch(queryString);

		const searchState: SearchState = {
			query: queryString,
			results: currentSearch()?.results,
			loading: true,
			// Keep legacy fields for backward compat
			author: undefined,
			before: undefined,
			after: undefined,
			channel: undefined,
		};
		updateSearch(searchState);

		const body = buildBackendSearchBody(editor.view.state, {
			channel: props.channel,
			room: props.room,
			sort: currentSearch()?.sort,
		}) as unknown as Record<string, unknown>;

		const res = await messagesService.search(body);
		updateSearch({ ...searchState, results: res, loading: false });
	};

	const handleCompletion = (c: Completion) => {
		if (c.type === "recent_search") {
			insertFilter(c.query, true, true);
		} else if (c.type === "text") {
			insertFilter(c.text, false, true);
		} else if (c.type === "node") {
			insertNode(c.node, true);
		}
	};

	const insertNode = (node: Node, shouldSubmit: boolean) => {
		const view = editor.view;
		const filter = activeFilter();
		if (!view || !filter) return;

		const tr = view.state.tr.replaceWith(filter.from, filter.to, node);
		tr.insertText(" ", tr.mapping.map(filter.from));
		tr.setMeta("skipAutocomplete", true);
		view.dispatch(tr);

		setActiveFilter(null);
		setHoveredIndex(0);
		view.focus();

		if (shouldSubmit) handleSubmit();
	};

	const insertFilter = (
		text: string,
		isRecent?: boolean,
		shouldSubmit?: boolean,
	) => {
		const view = editor.view;
		const filter = activeFilter();
		if (!view || !filter) return;

		if (isRecent) {
			const ctx = searchContext();
			const nodes = parseQueryToNodes(text, ctx);
			const paragraph = schema.nodes.paragraph.create(null, nodes);
			const tr = view.state.tr.replaceWith(
				0,
				view.state.doc.content.size,
				paragraph,
			);
			tr.setMeta("skipAutocomplete", true);
			view.dispatch(tr);
			setActiveFilter(null);
			setHoveredIndex(0);
			view.focus();
			handleSubmit();
			return;
		}

		const tr = view.state.tr.insertText(text, filter.from, filter.to);

		if (!text.endsWith(":")) {
			tr.setMeta("skipAutocomplete", true);
			setActiveFilter(null);
		}

		view.dispatch(tr);
		view.focus();
		if (shouldSubmit && !text.endsWith(":")) handleSubmit();
	};

	createEffect(() => {
		activeFilter();
		setHoveredIndex(0);
	});

	createEffect(() => {
		if (!activeFilter()) return;
		const items = dropdownRef()?.querySelectorAll(".autocomplete-item");
		if (items?.[hoveredIndex()]) {
			items[hoveredIndex()].scrollIntoView({ block: "nearest" });
		}
	});

	const [_autocompleteFocused, setAutocompleteFocused] = createSignal(false);

	const roomId = () => props.channel?.room_id ?? props.room?.id ?? null;
	const channelId = () => props.channel?.id ?? undefined;

	const editor = createBaseEditor({
		schema: schema as any,
		nodeViews: () => ({
			author: createFilterNodeView(
				"author",
				roomId(),
				channelId(),
				owner,
				editorFocused,
			),
			channel: createFilterNodeView(
				"channel",
				roomId(),
				channelId(),
				owner,
				editorFocused,
			),
			mentions: createFilterNodeView(
				"mentions",
				roomId(),
				channelId(),
				owner,
				editorFocused,
			),
			before: createFilterNodeView(
				"before",
				roomId(),
				channelId(),
				owner,
				editorFocused,
			),
			after: createFilterNodeView(
				"after",
				roomId(),
				channelId(),
				owner,
				editorFocused,
			),
			has: createFilterNodeView(
				"has",
				roomId(),
				channelId(),
				owner,
				editorFocused,
			),
			pinned: createFilterNodeView(
				"pinned",
				roomId(),
				channelId(),
				owner,
				editorFocused,
			),
		}),
		createState: (schema) => {
			let docContent: Node | undefined;
			const query = props.value ?? currentSearch()?.query;
			const ctx = searchContext();
			let selection: TextSelection | undefined;

			if (query) {
				const nodes = parseQueryToNodes(query, ctx);
				if (nodes.length > 0) {
					docContent = schema.nodes.doc.create(undefined, [
						schema.nodes.paragraph.create(undefined, nodes),
					]);
				}
			}

			if (docContent && (props.autofocus || query)) {
				// NOTE: maybe i want to select all search input content instead of placing the cursor at the end?
				selection = TextSelection.create(
					docContent,
					docContent.content.size - 1,
				);
			}

			return EditorState.create({
				schema: schema as any,
				doc: docContent,
				selection,
				plugins: [
					createPlaceholderPlugin(),
					history(),
					keymap({
						"Ctrl-z": undo,
						"Ctrl-Shift-z": redo,
					}),
					syntaxHighlightingPlugin(),
					autocompletePlugin((filter) => {
						if (filter && editor.view && !editor.view.hasFocus()) return;
						setActiveFilter(filter);
					}),
					new Plugin({
						props: {
							handleKeyDown(view, event) {
								const items = autocompleteItems();
								const { state } = view;
								const isAutocompleteOpen = hasSuggestions();

								// document open + paragraph open + paragraph close + document close = 4 nodes
								const isEmpty =
									!state.doc.textContent.length && state.doc.nodeSize === 4;

								if (isAutocompleteOpen && !isEmpty) {
									if (items.length > 0) {
										if (event.key === "ArrowDown") {
											setHoveredIndex((prev) => (prev + 1) % items.length);
											return true;
										} else if (event.key === "ArrowUp") {
											setHoveredIndex(
												(prev) => (prev + items.length - 1) % items.length,
											);
											return true;
										} else if (event.key === "Tab") {
											const item = items[hoveredIndex()];
											item.onSelect();
											return true;
										} else if (event.key === "Enter") {
											const item = items[hoveredIndex()];
											item.onSelect();
											handleSubmit();
											return true;
										}
									}

									if (event.key === "Escape") {
										event.preventDefault();
										setActiveFilter(null);
										return true;
									}
								}

								if (event.key === "Enter" && !event.shiftKey) {
									handleSubmit();
									return true;
								} else if (event.key === "Escape") {
									event.preventDefault();

									if (!isEmpty) {
										const tr = state.tr.delete(0, state.doc.content.size);
										view.dispatch(tr);
									} else {
										updateSearch(undefined);
										const chatInput = document.querySelector(
											".chat .ProseMirror",
										) as HTMLInputElement | null;
										chatInput?.focus();
									}

									return true;
								}

								return false;
							},
						},
					}),
					// doesnt seem to do anything?
					gapCursor(),
					new Plugin({
						state: {
							init() {
								return { q: "" };
							},
							apply(tr, prev, _oldState, state) {
								if (tr.docChanged) {
									const q = serializeToQuery(state);
									if (q !== prev.q) {
										props.onInput?.(q);
										return { q };
									}
								}
								return prev;
							},
						},
					}),
				],
			});
		},
		handleDOMEvents: {
			focus: (view: EditorView) => {
				if (isFirstFocus()) {
					setIsFirstFocus(false);
				} else {
					setActiveFilter(getFilterFromSelection(view.state));
				}
				setEditorFocused(true);
				return false;
			},
			blur: () => {
				setActiveFilter(null);
				setAutocompleteFocused(false);
				setEditorFocused(false);
				return false;
			},
		},
		autofocus: props.autofocus ?? !!currentSearch(),
	});

	// automatically submit the initial query if it exists
	onMount(() => {
		queueMicrotask(() => {
			if (props.value) {
				handleSubmit();
			}
		});
	});

	return (
		<div class="search-container" data-focus-ring-target>
			<div class="search-input" ref={setEditorRef}>
				<editor.View placeholder="search" />
			</div>
			<Icon src={icSearch} alt="" />
			<Portal mount={document.getElementById("overlay")!}>
				<Show when={hasSuggestions()}>
					<div
						ref={setDropdownRef}
						class="floating"
						style={{
							position: position.strategy,
							top: `${position.y ?? 0}px`,
							left: `${position.x ?? 0}px`,
							// width: `${(editorRef()?.offsetWidth || 300) * 2}px`,
							// TODO: handle responsive ui
							width: "600px",
						}}
					>
						<SearchAutocomplete
							filter={activeFilter()!}
							channel={props.channel}
							room={props.room}
							onCompletion={handleCompletion}
							hoveredIndex={hoveredIndex()}
							setHoveredIndex={setHoveredIndex}
							searchContext={searchContext()}
							onPointerDown={() => setAutocompleteFocused(true)}
							onBlur={() => setAutocompleteFocused(false)}
							autocompleteItems={autocompleteItems()}
							filterSuggestions={filterSuggestions()}
							recentSearches={recentSearches()}
							hasSuggestions={hasSuggestions()}
						/>
					</div>
				</Show>
			</Portal>
		</div>
	);
};
